import { isClosedCycle } from './cycleFills';
import type { Annotation, DerivedPropertySet, DrawingDocument, Graph, Provenance, StereoLigand } from '../model';

type UnknownRecord = Record<string, unknown>;

interface LegacyV9Document extends UnknownRecord {
  readonly schemaVersion: 'chem-drawing.v9';
  readonly graph: Graph;
  readonly coordinateSets: readonly UnknownRecord[];
  readonly atomLayer: UnknownRecord;
  readonly bondLayer: UnknownRecord;
  readonly formalChargeLayer: UnknownRecord;
  readonly atomLabelLayer: UnknownRecord;
  readonly aromaticityLayer: UnknownRecord;
  readonly stereoLayer: UnknownRecord;
  readonly bondLengthOverrideLayer: UnknownRecord;
}

const MIGRATED_PROVENANCE: Provenance = {
  source: 'migrated',
  engine: 'unknown',
  engineVersion: 'unknown',
  method: 'v9 persisted perception'
};

const ATOM_APPEARANCE_KEYS: ReadonlySet<string> = new Set(['color', 'colorByElement']);
const BOND_APPEARANCE_KEYS: ReadonlySet<string> = new Set(['color', 'lineWidth']);

function record(value: unknown, message: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(message);
  return value as UnknownRecord;
}

function nonEmptyString(value: unknown, message: string): string {
  if (typeof value !== 'string' || value.length === 0) throw new Error(message);
  return value;
}

function graphIds(graph: Graph): { nodeIds: Set<string>; edgeIds: Set<string> } {
  const nodeIds = new Set<string>();
  for (const node of graph.nodes) {
    if (!node.id || nodeIds.has(node.id)) {
      throw new Error('Every graph node must have a unique, non-empty id.');
    }
    nodeIds.add(node.id);
  }

  const edgeIds = new Set<string>();
  const connections = new Set<string>();
  for (const edge of graph.edges) {
    if (!edge.id || edgeIds.has(edge.id)) {
      throw new Error('Every graph edge must have a unique, non-empty id.');
    }
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      throw new Error(`Edge ${edge.id} references an atom that does not exist.`);
    }
    if (edge.source === edge.target) throw new Error(`Edge ${edge.id} is a self-loop.`);
    const connection = [edge.source, edge.target].sort().join('\u0000');
    if (connections.has(connection)) throw new Error(`Duplicate edge ${edge.id} is not supported.`);
    connections.add(connection);
    edgeIds.add(edge.id);
  }
  return { nodeIds, edgeIds };
}

function assertExactKeys(values: UnknownRecord, expected: ReadonlySet<string>, label: string): void {
  for (const id of expected) {
    if (!(id in values)) throw new Error(`${label} is missing ${id}.`);
  }
  for (const id of Object.keys(values)) {
    if (!expected.has(id)) throw new Error(`${label} references unknown id ${id}.`);
  }
}

function upgradeLegacyToV9(input: UnknownRecord): LegacyV9Document {
  let value = input;
  const graph = record(value.graph, 'The legacy graph is missing.');
  if (value.schemaVersion === 'chem-drawing.v6') {
    value = {
      ...value,
      schemaVersion: 'chem-drawing.v7',
      aromaticityLayer: {
        id: `aromaticity-migrated-${String(graph.id ?? 'graph')}`,
        revision: 1,
        graphId: graph.id,
        graphRevision: graph.revision,
        atomValues: {},
        bondValues: {}
      }
    };
  }
  if (value.schemaVersion === 'chem-drawing.v7') {
    value = {
      ...value,
      schemaVersion: 'chem-drawing.v8',
      stereoLayer: {
        id: `stereo-migrated-${String(graph.id ?? 'graph')}`,
        revision: 1,
        graphId: graph.id,
        graphRevision: graph.revision,
        atomValues: {},
        bondValues: {}
      }
    };
  }
  if (value.schemaVersion === 'chem-drawing.v8') {
    value = {
      ...value,
      schemaVersion: 'chem-drawing.v9',
      bondLengthOverrideLayer: {
        id: `bond-length-override-migrated-${String(graph.id ?? 'graph')}`,
        revision: 1,
        graphId: graph.id,
        graphRevision: graph.revision,
        values: {}
      }
    };
  }
  if (value.schemaVersion !== 'chem-drawing.v9') {
    throw new Error('Unsupported schemaVersion; expected chem-drawing.v6 through chem-drawing.v10.');
  }
  return value as LegacyV9Document;
}

function legacyLayerValues(layer: unknown, name: string): UnknownRecord {
  return record(
    record(layer, `The legacy ${name} layer is invalid.`).values,
    `The legacy ${name} layer values are invalid.`
  );
}

function reconstructLigands(
  graph: Graph,
  atomId: string
): readonly [StereoLigand, StereoLigand, StereoLigand, StereoLigand] {
  const neighbors = graph.edges.flatMap((edge) =>
    edge.source === atomId ? [edge.target] : edge.target === atomId ? [edge.source] : []
  );
  if (neighbors.length !== 3 && neighbors.length !== 4) {
    throw new Error('stereo-ordering-recalculation-required');
  }
  const ligands: StereoLigand[] = neighbors.map((neighbor) => ({ atomId: neighbor }));
  if (ligands.length === 3) ligands.push({ implicitHydrogen: true });
  return ligands as unknown as readonly [StereoLigand, StereoLigand, StereoLigand, StereoLigand];
}

export function migrateV9ToV10(legacy: LegacyV9Document): DrawingDocument {
  if (!Array.isArray(legacy.coordinateSets) || legacy.coordinateSets.length !== 1) {
    throw new Error('coordinate-set-choice-required');
  }

  const graph = legacy.graph;
  const atoms = legacyLayerValues(legacy.atomLayer, 'atom');
  const bonds = legacyLayerValues(legacy.bondLayer, 'bond');
  const wrappedCharges = legacyLayerValues(legacy.formalChargeLayer, 'formal charge');
  const atomLabelsV9 = legacyLayerValues(legacy.atomLabelLayer, 'atom label');
  const overridesV9 = legacyLayerValues(legacy.bondLengthOverrideLayer, 'bond length override');
  const aromaticity = record(legacy.aromaticityLayer, 'The legacy aromaticity layer is invalid.');
  const stereo = record(legacy.stereoLayer, 'The legacy stereo layer is invalid.');
  const atomStereo = record(stereo.atomValues, 'The legacy atom stereo values are invalid.');
  const bondStereo = record(stereo.bondValues, 'The legacy bond stereo values are invalid.');
  const coordinateSet = legacy.coordinateSets[0]!;

  const formalCharges = Object.fromEntries(
    Object.entries(wrappedCharges).map(([id, value]) => {
      const charge = record(value, `Legacy formal charge ${id} is invalid.`).formalCharge;
      return [id, charge];
    })
  );
  const atomLabels = Object.fromEntries(
    Object.entries(atomLabelsV9).map(([id, value]) => {
      const label = record(value, `Legacy atom label ${id} is invalid.`);
      return [id, { hydrogenDisplay: 'auto' as const, hydrogenDirection: label.hydrogenDirection }];
    })
  );
  const bondLengthOverrides = Object.fromEntries(Object.entries(overridesV9).map(([id]) => [id, true as const]));

  const tetrahedral: Record<string, DrawingDocument['structure']['stereochemistry']['tetrahedral'][string]> = {};
  const atomDescriptors: Record<string, 'R' | 'S'> = {};
  for (const [atomId, raw] of Object.entries(atomStereo)) {
    const value = record(raw, `Legacy atom stereo ${atomId} is invalid.`);
    const winding = value.parity;
    if (winding !== 'clockwise' && winding !== 'counterclockwise') {
      throw new Error(`Legacy atom stereo ${atomId} has invalid parity.`);
    }
    tetrahedral[atomId] = { ligands: reconstructLigands(graph, atomId), winding };
    if (value.descriptor === 'R' || value.descriptor === 'S') atomDescriptors[atomId] = value.descriptor;
  }

  const doubleBonds: Record<string, DrawingDocument['structure']['stereochemistry']['doubleBonds'][string]> = {};
  const bondStyles: Record<string, DrawingDocument['depiction']['bondStyles'][string]> = {};
  for (const [edgeId, raw] of Object.entries(bondStereo)) {
    const value = record(raw, `Legacy bond stereo ${edgeId} is invalid.`);
    if (value.type === 'wedge') {
      if (value.style !== 'solid' && value.style !== 'hashed') {
        throw new Error(`Legacy wedge ${edgeId} has invalid style.`);
      }
      bondStyles[edgeId] = {
        style: value.style === 'solid' ? 'solid-wedge' : 'hashed-wedge',
        fromAtomId: nonEmptyString(value.fromNodeId, `Legacy wedge ${edgeId} has no source atom.`)
      };
    } else if (value.type === 'double-bond') {
      const ids = value.stereoAtomIds;
      if (
        !Array.isArray(ids) ||
        ids.length !== 2 ||
        (value.configuration !== 'cis' && value.configuration !== 'trans')
      ) {
        throw new Error(`Legacy double-bond stereo ${edgeId} is invalid.`);
      }
      doubleBonds[edgeId] = {
        referenceAtomIds: [String(ids[0]), String(ids[1])],
        relativeConfiguration: value.configuration
      };
    } else if (value.type === 'crossed-double') {
      bondStyles[edgeId] = { style: 'crossed-double' };
    } else {
      throw new Error(`Legacy stereo bond ${edgeId} is invalid.`);
    }
  }

  const derivedPropertySets: DerivedPropertySet[] = [];
  const aromaticAtoms = Object.keys(record(aromaticity.atomValues, 'Legacy aromatic atoms are invalid.'));
  const aromaticBonds = Object.keys(record(aromaticity.bondValues, 'Legacy aromatic bonds are invalid.'));
  if (aromaticAtoms.length || aromaticBonds.length) {
    derivedPropertySets.push({
      id: `aromaticity-migrated-${graph.id}`,
      kind: 'aromaticity',
      structureId: graph.id,
      structureRevision: graph.revision,
      atomIds: aromaticAtoms,
      bondIds: aromaticBonds,
      provenance: MIGRATED_PROVENANCE
    });
  }
  if (Object.keys(atomDescriptors).length) {
    derivedPropertySets.push({
      id: `cip-migrated-${graph.id}`,
      kind: 'cip',
      structureId: graph.id,
      structureRevision: graph.revision,
      atomDescriptors,
      bondDescriptors: {},
      provenance: MIGRATED_PROVENANCE
    });
  }

  return {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id: graph.id,
      revision: graph.revision,
      graph,
      atoms: atoms as DrawingDocument['structure']['atoms'],
      bonds: bonds as DrawingDocument['structure']['bonds'],
      formalCharges: formalCharges as DrawingDocument['structure']['formalCharges'],
      stereochemistry: { tetrahedral, doubleBonds }
    },
    depiction: {
      id: `depiction-migrated-${graph.id}`,
      revision: 1,
      structureId: graph.id,
      structureRevision: graph.revision,
      coordinateSet: {
        id: nonEmptyString(coordinateSet.id, 'The legacy coordinate set has no id.'),
        kind: 'depiction-2d',
        structureId: graph.id,
        structureRevision: graph.revision,
        dimensions: 2,
        units: 'drawing-unit',
        width: coordinateSet.width as number,
        height: coordinateSet.height as number,
        coordinates: coordinateSet.coordinates as DrawingDocument['depiction']['coordinateSet']['coordinates']
      },
      atomLabels: atomLabels as DrawingDocument['depiction']['atomLabels'],
      bondStyles,
      bondLengthOverrides
    },
    annotations: [],
    derivedPropertySets
  };
}

function validateAnnotation(
  annotation: Annotation,
  nodeIds: ReadonlySet<string>,
  edgeIds: ReadonlySet<string>,
  coordinateSetId: string
): void {
  if (!annotation.id) throw new Error('Every annotation requires an id.');
  if (annotation.kind === 'shape' && annotation.shape === 'path') {
    if (
      !annotation.points ||
      annotation.points.length < (annotation.closed ? 3 : 2) ||
      annotation.points.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))
    ) {
      throw new Error(`Path annotation ${annotation.id} has too few nodes or invalid coordinates.`);
    }
    if (
      annotation.pathControls &&
      (annotation.pathControls.length !== annotation.points.length ||
        annotation.pathControls.some((c) =>
          [c.incoming, c.outgoing].some((p) => p && (!Number.isFinite(p.x) || !Number.isFinite(p.y)))
        ))
    ) {
      throw new Error(`Path annotation ${annotation.id} requires finite handles aligned with its nodes.`);
    }
  }
  if (annotation.kind === 'bond-angle') {
    if (annotation.coordinateSetId !== coordinateSetId || annotation.atomIds.some((id) => !nodeIds.has(id))) {
      throw new Error(`Bond-angle annotation ${annotation.id} has invalid references.`);
    }
    return;
  }
  if (annotation.kind === 'curved-arrow' || annotation.kind === 'reaction-arrow') {
    if (annotation.kind === 'curved-arrow' && annotation.controls !== undefined) {
      if (
        !Array.isArray(annotation.controls) ||
        annotation.controls.length !== 2 ||
        annotation.controls.some((point) => !point || !Number.isFinite(point.x) || !Number.isFinite(point.y))
      ) {
        throw new Error(`Curved-arrow annotation ${annotation.id} requires two finite control points.`);
      }
    }
    const anchorInvalid = (anchor: typeof annotation.from) =>
      (anchor.kind === 'atom' && !nodeIds.has(anchor.atomId)) ||
      (anchor.kind === 'bond' && !edgeIds.has(anchor.bondId)) ||
      (anchor.kind === 'lone-pair' && !nodeIds.has(anchor.atomId));
    if (anchorInvalid(annotation.from) || anchorInvalid(annotation.to)) {
      throw new Error(`${annotation.kind} annotation ${annotation.id} has an invalid anchor.`);
    }
    return;
  }
  if (annotation.kind === 'shape') {
    if (annotation.callout) {
      const { tip, variant, paragraphs } = annotation.callout;
      if (
        !annotation.bounds ||
        !tip ||
        !Number.isFinite(tip.x) ||
        !Number.isFinite(tip.y) ||
        !['rect', 'rounded', 'ellipse', 'cloud', 'burst', 'leader', 'chevron', 'notched-arrow'].includes(variant) ||
        !Array.isArray(paragraphs) ||
        !paragraphs.length
      )
        throw new Error(`Callout ${annotation.id} has invalid bubble geometry or text.`);
      validateAnnotation(
        { id: annotation.id, kind: 'text', position: tip, paragraphs },
        nodeIds,
        edgeIds,
        coordinateSetId
      );
    }
    if (annotation.rotation !== undefined && !Number.isFinite(annotation.rotation))
      throw new Error('Shape rotation must be finite.');
    return;
  }
  if (annotation.kind === 'text') {
    const { position, paragraphs } = annotation;
    if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) {
      throw new Error(`Text annotation ${annotation.id} requires a finite position.`);
    }
    const runInvalid = (run: (typeof paragraphs)[number]['runs'][number]) =>
      !run.text ||
      /[\n\r]/.test(run.text) ||
      (run.fontSize !== undefined && !(Number.isFinite(run.fontSize) && run.fontSize > 0));
    if (
      !Array.isArray(paragraphs) ||
      paragraphs.length === 0 ||
      paragraphs.some((paragraph) => !Array.isArray(paragraph.runs) || paragraph.runs.some(runInvalid))
    ) {
      throw new Error(`Text annotation ${annotation.id} has invalid paragraphs.`);
    }
    return;
  }
  const target = annotation.target;
  if (
    (target.kind === 'atom' && !nodeIds.has(target.atomId)) ||
    (target.kind === 'bond' && !edgeIds.has(target.bondId)) ||
    (target.kind === 'atom-set' && target.atomIds.some((id) => !nodeIds.has(id))) ||
    (target.kind === 'bond-set' && target.bondIds.some((id) => !edgeIds.has(id))) ||
    (target.kind === 'region-2d' && target.coordinateSetId !== coordinateSetId)
  ) {
    throw new Error(`Annotation ${annotation.id} has an invalid target.`);
  }
}

function validateV10(candidate: DrawingDocument): DrawingDocument {
  if (candidate.schemaVersion !== 'chem-drawing.v10') {
    throw new Error('Unsupported schemaVersion; expected chem-drawing.v10.');
  }
  const { structure, depiction } = candidate;
  if (
    !structure ||
    !depiction ||
    !Array.isArray(candidate.annotations) ||
    !Array.isArray(candidate.derivedPropertySets)
  ) {
    throw new Error('The v10 document is missing structure, depiction, annotations, or derived properties.');
  }
  if (
    !structure.id ||
    !Number.isInteger(structure.revision) ||
    structure.revision < 1 ||
    structure.graph.id !== structure.id ||
    !Number.isInteger(structure.graph.revision) ||
    structure.graph.revision < 1
  ) {
    throw new Error('The structure and graph identity or revision is invalid.');
  }
  const { nodeIds, edgeIds } = graphIds(structure.graph);
  assertExactKeys(structure.atoms as UnknownRecord, nodeIds, 'Atom map');
  assertExactKeys(structure.formalCharges as UnknownRecord, nodeIds, 'Formal-charge map');
  assertExactKeys(structure.bonds as UnknownRecord, edgeIds, 'Bond map');

  for (const [id, atom] of Object.entries(structure.atoms)) {
    if (
      !Number.isInteger(atom.atomicNumber) ||
      atom.atomicNumber < 1 ||
      atom.atomicNumber > 118 ||
      (atom.isotope !== undefined && (!Number.isInteger(atom.isotope) || atom.isotope < 1 || atom.isotope > 300)) ||
      (atom.radicalElectronCount !== undefined &&
        (!Number.isInteger(atom.radicalElectronCount) ||
          atom.radicalElectronCount < 1 ||
          atom.radicalElectronCount > 3))
    ) {
      throw new Error(`Atom ${id} has invalid properties.`);
    }
  }
  for (const [id, bond] of Object.entries(structure.bonds)) {
    // ADR 0022: a hydrogen bond has order 0; so does a hapto bond (ADR 0026), and only they do.
    const orderZero = bond.type === 'hydrogen' || bond.type === 'hapto' || bond.type === 'agostic';
    if (
      !['covalent', 'dative', 'hydrogen', 'hapto', 'agostic'].includes(bond.type) ||
      (orderZero ? bond.order !== 0 : ![1, 2, 3, 4, 5, 6].includes(bond.order))
    ) {
      throw new Error(`Bond ${id} has invalid properties.`);
    }
    // ADR 0026 D1: a hapto bond's ligand is two or more atoms of the drawing; no other bond has one.
    if (
      bond.type === 'hapto'
        ? (bond.ligandAtomIds?.length ?? 0) < 2 || bond.ligandAtomIds!.some((atomId) => !structure.atoms[atomId])
        : bond.ligandAtomIds !== undefined
    ) {
      throw new Error(`Bond ${id} has an invalid ligand.`);
    }
    if (bond.type === 'dative') {
      // B4 (#273): a dative bond is order 1 and directed -- `donorAtomId` must be one of its endpoints.
      const edge = structure.graph.edges.find((candidate) => candidate.id === id);
      if (bond.order !== 1 || !edge || ![edge.source, edge.target].includes(bond.donorAtomId ?? '')) {
        throw new Error(`Dative bond ${id} has an invalid donor.`);
      }
    } else if (bond.donorAtomId !== undefined) {
      throw new Error(`Bond ${id} carries a donor but is not dative.`);
    }
  }
  for (const [id, charge] of Object.entries(structure.formalCharges)) {
    if (!Number.isInteger(charge)) throw new Error(`Atom ${id} has an invalid formal charge.`);
  }

  if (
    depiction.structureId !== structure.id ||
    depiction.structureRevision !== structure.revision ||
    !depiction.id ||
    !Number.isInteger(depiction.revision) ||
    depiction.revision < 1
  ) {
    throw new Error('The depiction does not match the structure revision.');
  }
  const coordinates = depiction.coordinateSet;
  if (
    coordinates.kind !== 'depiction-2d' ||
    coordinates.structureId !== structure.id ||
    coordinates.structureRevision !== structure.revision ||
    coordinates.dimensions !== 2 ||
    coordinates.units !== 'drawing-unit' ||
    !Number.isFinite(coordinates.width) ||
    coordinates.width <= 0 ||
    !Number.isFinite(coordinates.height) ||
    coordinates.height <= 0
  ) {
    throw new Error('The 2D coordinate set is invalid or does not match the structure revision.');
  }
  if (coordinates.depths !== undefined) {
    const depths = record(coordinates.depths, 'Depths must be an atom-keyed map.');
    for (const [id, depth] of Object.entries(depths)) {
      if (!nodeIds.has(id) || typeof depth !== 'number' || !Number.isFinite(depth)) {
        throw new Error(`Atom ${id} does not have a valid depth.`);
      }
    }
  }
  assertExactKeys(coordinates.coordinates as UnknownRecord, nodeIds, 'Coordinate map');
  for (const [id, point] of Object.entries(coordinates.coordinates)) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      throw new Error(`Atom ${id} does not have a valid coordinate.`);
    }
  }

  for (const [id, label] of Object.entries(depiction.atomLabels)) {
    if (!nodeIds.has(id)) throw new Error(`Atom label references unknown atom ${id}.`);
    if (!['auto', 'show', 'hide'].includes(label.hydrogenDisplay)) {
      throw new Error(`Atom ${id} has an invalid hydrogen-display mode.`);
    }
    if (label.hydrogenDirection) {
      const { x, y } = label.hydrogenDirection;
      if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(Math.hypot(x, y) - 1) > 1e-2) {
        throw new Error(`Atom ${id} has an invalid hydrogen direction.`);
      }
    }
    if (label.resonanceCharge !== undefined && !Number.isFinite(label.resonanceCharge)) {
      throw new Error(`Atom ${id} has an invalid resonance charge.`);
    }
  }
  for (const [id, style] of Object.entries(depiction.bondStyles)) {
    const edge = structure.graph.edges.find((candidateEdge) => candidateEdge.id === id);
    if (!edge) {
      throw new Error(`Bond style references invalid bond ${id}.`);
    }
    if (style.style === 'crossed-double') {
      // B3 (#271): a crossed double bond -- valid only on an order-2 bond; carries no direction.
      if (structure.bonds[id]?.order !== 2) {
        throw new Error(`Crossed-double style on non-double bond ${id}.`);
      }
    } else if (style.style === 'double-bond-side') {
      // #453: which ring a fusion double bond's second line faces -- valid only on an order-2
      // bond, naming a real atom (its own ring membership is the mutation's concern, not this
      // structural check).
      if (structure.bonds[id]?.order !== 2 || structure.atoms[style.facingAtomId] === undefined) {
        throw new Error(`Double-bond-side style references invalid bond or atom ${id}.`);
      }
    } else if (style.style === 'wavy') {
      // ADR 0022 D5: a wavy bond -- a single covalent bond naming its stereocentre end.
      const bond = structure.bonds[id];
      if (bond?.order !== 1 || bond.type !== 'covalent' || ![edge.source, edge.target].includes(style.fromAtomId)) {
        throw new Error(`Wavy style references invalid bond ${id}.`);
      }
    } else if (
      !['solid-wedge', 'hashed-wedge'].includes(style.style) ||
      ![edge.source, edge.target].includes(style.fromAtomId)
    ) {
      throw new Error(`Wedge style references invalid bond ${id}.`);
    }
  }
  for (const [id, overridden] of Object.entries(depiction.bondLengthOverrides)) {
    if (!edgeIds.has(id) || overridden !== true) {
      throw new Error(`Bond-length override references invalid bond ${id}.`);
    }
  }
  if (depiction.cycleFills !== undefined && !Array.isArray(depiction.cycleFills))
    throw new Error('Invalid cycle fills.');
  const filledCycles = new Set<string>();
  for (const fill of depiction.cycleFills ?? []) {
    if (
      !fill ||
      Object.keys(fill).some((key) => !['atomIds', 'color'].includes(key)) ||
      !Array.isArray(fill.atomIds) ||
      !fill.atomIds.every((id) => typeof id === 'string') ||
      !isClosedCycle(fill.atomIds, structure.graph.edges) ||
      typeof fill.color !== 'string' ||
      !fill.color.trim()
    ) {
      throw new Error('Invalid cycle fill: expected a closed ring and a colour.');
    }
    const key = JSON.stringify([...fill.atomIds].sort());
    if (filledCycles.has(key)) throw new Error('Duplicate cycle fill.');
    filledCycles.add(key);
  }
  // #868 -- per-atom / per-bond appearance: real atoms and bonds, a colour, by-element flag, a
  // width in points; nothing else (no bond dashes).
  const validColor = (value: unknown) => value === undefined || (typeof value === 'string' && value.length > 0);
  for (const [id, appearance] of Object.entries(depiction.atomAppearance ?? {})) {
    if (!nodeIds.has(id)) throw new Error(`Atom appearance references unknown atom ${id}.`);
    if (Object.keys(appearance).some((key) => !ATOM_APPEARANCE_KEYS.has(key))) {
      throw new Error(`Atom ${id} appearance has an unknown property.`);
    }
    if (!validColor(appearance.color) || !['undefined', 'boolean'].includes(typeof appearance.colorByElement)) {
      throw new Error(`Atom ${id} has an invalid appearance.`);
    }
  }
  for (const [id, appearance] of Object.entries(depiction.bondAppearance ?? {})) {
    if (!edgeIds.has(id)) throw new Error(`Bond appearance references unknown bond ${id}.`);
    if (Object.keys(appearance).some((key) => !BOND_APPEARANCE_KEYS.has(key))) {
      throw new Error(`Bond ${id} appearance has an unknown property.`);
    }
    const width = appearance.lineWidth;
    if (
      !validColor(appearance.color) ||
      (width !== undefined && !(Number.isFinite(width) && width > 0 && width <= 72))
    ) {
      throw new Error(`Bond ${id} has an invalid appearance.`);
    }
  }

  for (const [atomId, stereo] of Object.entries(structure.stereochemistry.tetrahedral)) {
    const neighbors = new Set(
      structure.graph.edges.flatMap((edge) =>
        edge.source === atomId ? [edge.target] : edge.target === atomId ? [edge.source] : []
      )
    );
    const ligandAtomIds = stereo.ligands.flatMap((ligand) => ('atomId' in ligand ? [ligand.atomId] : []));
    const implicitHydrogens = stereo.ligands.filter((ligand) => 'implicitHydrogen' in ligand).length;
    if (
      !nodeIds.has(atomId) ||
      stereo.ligands.length !== 4 ||
      !['clockwise', 'counterclockwise'].includes(stereo.winding) ||
      ligandAtomIds.some((id) => !neighbors.has(id)) ||
      new Set(ligandAtomIds).size !== ligandAtomIds.length ||
      implicitHydrogens > 1 ||
      ligandAtomIds.length + implicitHydrogens !== 4
    ) {
      throw new Error(`Tetrahedral stereo references invalid atom ${atomId}.`);
    }
  }
  for (const [bondId, stereo] of Object.entries(structure.stereochemistry.doubleBonds)) {
    const edge = structure.graph.edges.find((candidateEdge) => candidateEdge.id === bondId);
    const [sourceReference, targetReference] = stereo.referenceAtomIds;
    const isNeighbor = (atomId: string, referenceId: string): boolean =>
      structure.graph.edges.some(
        (candidateEdge) =>
          candidateEdge.id !== bondId &&
          ((candidateEdge.source === atomId && candidateEdge.target === referenceId) ||
            (candidateEdge.target === atomId && candidateEdge.source === referenceId))
      );
    if (
      !edge ||
      structure.bonds[bondId]?.order !== 2 ||
      stereo.referenceAtomIds.length !== 2 ||
      !sourceReference ||
      !targetReference ||
      sourceReference === targetReference ||
      !isNeighbor(edge.source, sourceReference) ||
      !isNeighbor(edge.target, targetReference) ||
      !['cis', 'trans'].includes(stereo.relativeConfiguration)
    ) {
      throw new Error(`Double-bond stereo references invalid bond ${bondId}.`);
    }
  }

  for (const annotation of candidate.annotations) {
    validateAnnotation(annotation, nodeIds, edgeIds, coordinates.id);
    if (
      (annotation.kind === 'text' && annotation.attachment !== undefined) ||
      (annotation.kind === 'shape' && annotation.callout?.attachment !== undefined)
    ) {
      const target = annotation.kind === 'text' ? annotation.attachment! : annotation.callout!.attachment!;
      if (!target || typeof target !== 'object') throw new Error(`Callout ${annotation.id} has an invalid attachment.`);
      if (
        (target.kind === 'atom' && !nodeIds.has(target.atomId)) ||
        (target.kind === 'atom-set' &&
          (!Array.isArray(target.atomIds) ||
            !target.atomIds.length ||
            new Set(target.atomIds).size !== target.atomIds.length ||
            target.atomIds.some((id: string) => !nodeIds.has(id)))) ||
        (target.kind === 'bond' && !edgeIds.has(target.bondId)) ||
        (target.kind === 'annotation' &&
          !candidate.annotations.some(
            (item) => item.id === target.annotationId && item.kind === 'shape' && !item.callout
          )) ||
        !['atom', 'atom-set', 'bond', 'annotation'].includes(target.kind)
      )
        throw new Error(`Callout ${annotation.id} has an invalid attachment.`);
    }
  }
  for (const result of candidate.derivedPropertySets) {
    if (result.structureId !== structure.id || result.structureRevision !== structure.revision) {
      throw new Error(`Derived property set ${result.id} does not match the structure revision.`);
    }
    const referencedAtoms: readonly string[] =
      result.kind === 'aromaticity'
        ? result.atomIds
        : result.kind === 'cip'
          ? Object.keys(result.atomDescriptors)
          : Object.keys(result.charges);
    if (referencedAtoms.some((id) => !nodeIds.has(id))) {
      throw new Error(`Derived property set ${result.id} references an unknown atom.`);
    }
    if (result.kind === 'aromaticity' && result.bondIds.some((id: string) => !edgeIds.has(id))) {
      throw new Error(`Derived property set ${result.id} references an unknown bond.`);
    }
    if (result.kind === 'cip' && Object.keys(result.bondDescriptors).some((id) => !edgeIds.has(id))) {
      throw new Error(`Derived property set ${result.id} references an unknown bond.`);
    }
  }
  return candidate;
}

export function validateDrawingDocument(value: unknown): DrawingDocument {
  const input = record(value, 'The JSON root must be an object.');
  const candidate =
    input.schemaVersion === 'chem-drawing.v10'
      ? (input as unknown as DrawingDocument)
      : migrateV9ToV10(upgradeLegacyToV9(input));
  return validateV10(candidate);
}
